import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Alert, Autocomplete, Box, Grid, TextField, Typography } from '@mui/material';
import useGetListAppInstance from 'redux/slices/appInstance/hooks/useGetListAppInstance';
import useGetDetailAppInstance from 'redux/slices/appInstance/hooks/useGetDetailAppInstance';
import { getPipedContainerAppIds } from 'redux/slices/pipelines/helpers/getPipedContainerAppIds';
import { computeTargetSelectionUpdate } from './computeTargetSelectionUpdate';

// A large single page — there is no dedicated "all app instances" endpoint, and the
// wizard needs the full selectable set for the target step (not a paginated table).
const ALL_APP_INSTANCES_PAGE_SIZE = 500;

// Wizard step 2 — two-level target pick: App Instance -> its ContainerApp (RV-2: there is
// no flat container-list endpoint). Containers already bound to a live Pipeline are
// excluded (AC-6, Wave 1's 1:1 constraint), derived client-side by subtracting the
// ContainerAppIds present in the grouped pipeline list (RV-1 payload) from the selected
// instance's ContainerApps. `excludePipelineId` lets the Edit dialog keep its own current
// container selectable (not used by the create-only wizard).
//
// R1/AC-2 (pre-impl review) — this step is shared by TWO callers: PipelineWizard (Formik)
// and PipelineEditDialog (a plain `useState`). Both must pass a `setValues` updater-capable
// setter (Formik's `setValues` / the dialog's own `useState` setter) rather than
// `setFieldValue`, so the App-Instance selection is applied as ONE atomic object update
// (via `computeTargetSelectionUpdate`) instead of two sequential field writes — the latter
// is what caused the stale-`values` re-validation bug under Formik 2.4.9 (see spec
// §Problem). Using the updater form (`setValues(prev => …)`) is also correct under plain
// `useState`, which has no notion of Formik's render-closure `values` at all.
const TargetContainerStep = ({ values, errors, touched, setValues, excludePipelineId }) => {
	const { list: pipelineList } = useSelector(s => s.pipeline);
	const pipedContainerAppIds = useMemo(() => getPipedContainerAppIds(pipelineList, excludePipelineId), [pipelineList, excludePipelineId]);

	const { data: appInstanceData, isLoading: isLoadingInstances } = useGetListAppInstance({
		pageSize: ALL_APP_INSTANCES_PAGE_SIZE,
		pageIndex: 1,
		searchTerm: ''
	});
	const appInstances = appInstanceData?.data?.Items ?? [];

	const { data: appInstanceDetail, isLoading: isLoadingContainers } = useGetDetailAppInstance(values.AppInstanceId);
	const allContainers = appInstanceDetail?.data?.ContainerApps ?? [];
	const availableContainers = allContainers.filter(c => !pipedContainerAppIds.has(c.Id));

	const selectedAppInstance = appInstances.find(i => i.Id === values.AppInstanceId) ?? null;
	const selectedContainer = availableContainers.find(c => c.Id === values.ContainerAppId) ?? null;

	const isAllTaken = !!values.AppInstanceId && !isLoadingContainers && allContainers.length > 0 && availableContainers.length === 0;

	return (
		<Grid container spacing={5}>
			<Grid item xs={12} sm={6}>
				<Typography component="p" color="text.primary" fontSize={14} mb={2}>
					App Instance
					<Box component="span" ml={1} sx={{ color: 'error.main' }}>
						*
					</Box>
				</Typography>
				<Autocomplete
					fullWidth
					loading={isLoadingInstances}
					options={appInstances}
					getOptionLabel={option => option?.Name ?? ''}
					isOptionEqualToValue={(option, value) => option?.Id === value?.Id}
					value={selectedAppInstance}
					onChange={(_e, option) => {
						// R1/AC-2 — ONE atomic update via the updater form, never two sequential
						// setFieldValue calls (that's the bug this replaces).
						setValues(prev => computeTargetSelectionUpdate(prev, option?.Id));
					}}
					renderInput={params => (
						<TextField
							{...params}
							placeholder="Select an App Instance"
							error={touched?.AppInstanceId && !!errors?.AppInstanceId}
							helperText={touched?.AppInstanceId ? errors?.AppInstanceId : ''}
							inputProps={{ ...params.inputProps, 'aria-label': 'App Instance' }}
						/>
					)}
				/>
			</Grid>

			<Grid item xs={12} sm={6}>
				<Typography component="p" color="text.primary" fontSize={14} mb={2}>
					Target container
					<Box component="span" ml={1} sx={{ color: 'error.main' }}>
						*
					</Box>
				</Typography>
				<Autocomplete
					fullWidth
					disabled={!values.AppInstanceId}
					loading={isLoadingContainers}
					options={availableContainers}
					getOptionLabel={option => option?.ContainerAppName ?? ''}
					isOptionEqualToValue={(option, value) => option?.Id === value?.Id}
					value={selectedContainer}
					onChange={(_e, option) => setValues(prev => ({ ...prev, ContainerAppId: option?.Id ?? '' }))}
					renderInput={params => (
						<TextField
							{...params}
							placeholder={values.AppInstanceId ? 'Select a container' : 'Select an App Instance first'}
							error={touched?.ContainerAppId && !!errors?.ContainerAppId}
							helperText={touched?.ContainerAppId ? errors?.ContainerAppId : ''}
							inputProps={{ ...params.inputProps, 'aria-label': 'Target container' }}
						/>
					)}
				/>
			</Grid>

			{isAllTaken && (
				<Grid item xs={12}>
					<Alert severity="warning">
						Every container on this App Instance already has a live Pipeline. Choose a different App Instance, free up a container by deleting its existing Pipeline, or
						add a new container to this instance first.
					</Alert>
				</Grid>
			)}
		</Grid>
	);
};

export default React.memo(TargetContainerStep);

TargetContainerStep.propTypes = {
	values: PropTypes.object,
	errors: PropTypes.object,
	touched: PropTypes.object,
	setValues: PropTypes.func,
	excludePipelineId: PropTypes.string
};
